<script lang="ts">
    import { format, fromUnixTime, startOfDay, startOfWeek } from 'date-fns';
    import { dayKind, dayKey, isDayChanged as isChanged, isNotableChange, NOTABLE_XSS_CHANGE } from './AdaptForecastDayCard.svelte';

    /* eslint-disable @typescript-eslint/no-explicit-any */
    // What saving a successfully adapted forecast changes, for the Summary tab. The same day pairing as the Details tab.
    export let before: any[]
    export let after: any[]
    export let plan: any = null
    export let title = 'Training Forecast Adapted Successfully'

    const hours = (d: any) => d ? (d.adu > 0 ? d.adu : d.duration ?? 0) : 0
    const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`
    const fixed = (n: number, digits = 0) => n.toFixed(digits)

    $: pairs = (() => {
        const byDay = new Map<string, { before: any | null, after: any | null }>()
        for (const [side, days] of [['before', before], ['after', after]] as const) {
            for (const day of days ?? []) {
                const row = byDay.get(dayKey(day)) ?? { before: null, after: null }
                row[side] = row[side] ?? day
                byDay.set(dayKey(day), row)
            }
        }
        return [...byDay.values()]
    })()

    $: changed = pairs.filter(p => isChanged(p.before, p.after))
    $: added = changed.filter(p => dayKind(p.before) === 'rest' && dayKind(p.after) !== 'rest').length
    $: removed = changed.filter(p => dayKind(p.before) !== 'rest' && dayKind(p.after) === 'rest').length

    // The changes worth a look (same rule as the Details tab's Show Notable Changes Only).
    $: notable = changed.filter(p => isNotableChange(p.before, p.after))
    $: minor = changed.length - notable.length
    $: swappedToHigh = notable.filter(p => dayKind(p.before) === 'low' && dayKind(p.after) === 'high').length
    $: swappedToLow = notable.filter(p => dayKind(p.before) === 'high' && dayKind(p.after) === 'low').length
    // Days that kept their kind and are notable only because XSS moved more than 5%.
    const xssOnly = (p: any) => dayKind(p.before) !== 'rest' && dayKind(p.after) !== 'rest'
        && !(dayKind(p.before) === 'low' && dayKind(p.after) === 'high') && !(dayKind(p.before) === 'high' && dayKind(p.after) === 'low')
    $: xssUp = notable.filter(p => xssOnly(p) && (p.after.xss ?? 0) > (p.before.xss ?? 0)).length
    $: xssDown = notable.filter(p => xssOnly(p) && (p.after.xss ?? 0) < (p.before.xss ?? 0)).length

    const total = (days: any[], f: (d: any) => number) => (days ?? []).reduce((sum, d) => sum + (dayKind(d) === 'rest' ? 0 : f(d)), 0)
    $: hoursBefore = total(pairs.map(p => p.before).filter(Boolean), hours)
    $: hoursAfter = total(pairs.map(p => p.after).filter(Boolean), hours)
    $: xssBefore = total(pairs.map(p => p.before).filter(Boolean), d => d.xss ?? 0)
    $: xssAfter = total(pairs.map(p => p.after).filter(Boolean), d => d.xss ?? 0)

    $: maxWeeklyHours = plan?.settings?.maxWeeklyHours ?? null
    $: peakWeek = (() => {
        const weeks = new Map<number, number>()
        for (const d of after ?? []) {
            if (d.weekly_hours > 0) weeks.set(startOfWeek(fromUnixTime(d.t), { weekStartsOn: 1 }).getTime(), Math.max(weeks.get(startOfWeek(fromUnixTime(d.t), { weekStartsOn: 1 }).getTime()) ?? 0, d.weekly_hours))
        }
        return weeks.size ? Math.max(...weeks.values()) : null
    })()

    // The plan's target day: where the adapted forecast has to reach the goal, race or event.
    $: eventDay = [...(after ?? [])].reverse().find(d => (d.state ?? []).includes('event_A')) ?? null
    $: targets = (() => {
        const d = eventDay
        if (!d) return [] as string[]
        const mode = plan?.selected_mode
        if ((mode === 'target_focus' || mode === 'target_race') && d.target_ftp > 0) {
            const rows = [
                `Threshold Power ${fixed(d.ftp)} W (target ${fixed(d.target_ftp)} W)`,
                `High Intensity Energy ${fixed(d.hie / 1000, 1)} kJ (target ${fixed(d.target_hie / 1000, 1)} kJ)`,
                `Peak Power ${fixed(d.pp)} W (target ${fixed(d.target_pp)} W)`,
            ]
            if (mode === 'target_focus' && plan?.goal_info?.targets_mode !== 'manual' && d.target_fp > 0 && d.fp > 0) {
                rows.push(`${Math.round(d.sfd / 60)} Minute Focus Power ${fixed(d.fp)} W (target ${fixed(d.target_fp)} W)`)
            }
            return rows
        }
        return [['Low', d.tls?.l, d.ltl_target], ['High', d.tls?.h, d.htl_target], ['Peak', d.tls?.p, d.ptl_target]]
            .filter(([, load, target]) => target > 0 && load > 0)
            .map(([label, load, target]) => `${label} Training Load ${fixed(load as number)} (target ${fixed(target as number)})`)
    })()

    $: firstDate = pairs.length ? fromUnixTime((pairs[0].after ?? pairs[0].before).t) : null
</script>

<div class="rounded-md border border-green-200 border-l-4 border-l-green-600 bg-green-50 p-4 mb-3 text-sm text-slate-800 text-left">
    <div class="flex items-center gap-2 text-base font-bold text-green-800">
        <i class="fa-solid fa-circle-check"></i>{title}
    </div>
    <p class="mt-2">
        {#if targets.length > 0}
            Xert built a plan that reaches your target{eventDay ? ` by ${format(startOfDay(fromUnixTime(eventDay.t)), 'MMMM d')}` : ''}.
        {:else}
            Xert rebuilt your plan within your availability, intensity marks and recovery limits.
        {/if}
        Nothing changes until you save.
    </p>

    <div class="mt-3 font-semibold">When you save{firstDate ? `, from ${format(firstDate, 'MMMM d')}` : ''}:</div>
    <ul class="mt-1 list-disc pl-5 space-y-0.5">
        {#if changed.length === 0}
            <li>No days change. Your plan already fits your availability.</li>
        {:else}
            <li>{changed.length} of {pairs.length} activities were changed, but only {notable.length} {notable.length === 1 ? 'was' : 'were'} notable</li>
            {#if notable.length > 0}
                <li>
                    Notable changes:
                    <ul class="list-[circle] pl-5">
                        {#if added > 0}<li>{plural(added, 'workout')} added on days that were rest days</li>{/if}
                        {#if removed > 0}<li>{plural(removed, 'workout')} {removed === 1 ? 'becomes a rest day' : 'become rest days'}</li>{/if}
                        {#if swappedToHigh > 0}<li>{plural(swappedToHigh, 'day')} {swappedToHigh === 1 ? 'goes' : 'go'} from Low to High Intensity</li>{/if}
                        {#if swappedToLow > 0}<li>{plural(swappedToLow, 'day')} {swappedToLow === 1 ? 'goes' : 'go'} from High to Low Intensity</li>{/if}
                        {#if xssUp > 0}<li>{plural(xssUp, 'workout')} {xssUp === 1 ? 'gains' : 'gain'} more than {Math.round(NOTABLE_XSS_CHANGE * 100)}% XSS</li>{/if}
                        {#if xssDown > 0}<li>{plural(xssDown, 'workout')} {xssDown === 1 ? 'loses' : 'lose'} more than {Math.round(NOTABLE_XSS_CHANGE * 100)}% XSS</li>{/if}
                    </ul>
                </li>
            {/if}
            {#if minor > 0}<li>{plural(minor, 'minor adjustment')} (a few minutes or under {Math.round(NOTABLE_XSS_CHANGE * 100)}% XSS)</li>{/if}
        {/if}
        <li>Planned training: {fixed(hoursBefore, 1)} h to {fixed(hoursAfter, 1)} h, and {fixed(xssBefore)} XSS to {fixed(xssAfter)} XSS</li>
        <li>Pinned and completed workouts are kept</li>
    </ul>

    {#if targets.length > 0}
        <div class="mt-3 font-semibold">By your target date, the plan reaches:</div>
        <ul class="mt-1 list-disc pl-5 space-y-0.5">
            {#each targets as item}<li>{item}</li>{/each}
        </ul>
    {/if}

    {#if peakWeek !== null}
        <div class="mt-3 font-semibold">Weekly hours:</div>
        <ul class="mt-1 list-disc pl-5 space-y-0.5">
            <li>
                Your busiest week is {fixed(peakWeek, 1)} h{maxWeeklyHours ? `, within your Max Weekly Hours of ${maxWeeklyHours} h` : ''}
            </li>
        </ul>
    {/if}

    <p class="mt-3 text-slate-600">Open the Chart or Details tab to review the plan before saving.</p>
</div>
